import { io } from 'socket.io-client';
import requests from './httpService';

let socket = null;
let currentNoteId = null;

/**
 * Get socket server URL from API base URL
 */
const getSocketURL = () => {
  try {
    const baseURL = requests.baseURL();
    if (!baseURL) {
      return 'http://localhost:5000';
    }
    // Remove /api from baseURL if present, Socket.IO runs on root
    if (baseURL.endsWith('/api')) {
      return baseURL.replace('/api', '');
    }
    // If baseURL doesn't end with /api, return as is (assuming it's already the server root)
    return baseURL.replace('/api', '') || 'http://localhost:5000';
  } catch (err) {
    console.error('Error getting socket URL:', err);
    return 'http://localhost:5000';
  }
};

/**
 * Initialize socket connection
 */
const initializeSocket = () => {
  if (socket && socket.connected) {
    return socket;
  }

  const token = localStorage.getItem('id_token');
  if (!token) {
    console.error('No token found for socket authentication');
    return null;
  }

  const socketURL = getSocketURL();
  console.log('Connecting to socket server:', socketURL);

  socket = io(socketURL, {
    auth: {
      token: token,
    },
    transports: ['websocket', 'polling'],
    reconnection: true,
    reconnectionDelay: 1000,
    reconnectionAttempts: 5,
  });

  socket.on('connect', () => {
    console.log('Socket connected:', socket.id);
  });

  socket.on('disconnect', (reason) => {
    console.log('Socket disconnected:', reason);
  });

  socket.on('connect_error', (error) => {
    console.error('Socket connection error:', error);
  });

  socket.on('error', (error) => {
    console.error('Socket error:', error);
  });

  return socket;
};

/**
 * Join a note chat room
 */
const joinNoteChat = (noteId, callbacks = {}) => {
  if (!socket || !socket.connected) {
    socket = initializeSocket();
    if (!socket) {
      console.error('Failed to initialize socket');
      if (callbacks.onError) callbacks.onError({ message: 'Failed to connect to chat server' });
      return;
    }
  }

  // Leave previous room if switching notes
  if (currentNoteId && currentNoteId !== noteId) {
    socket.emit('leave-note-chat', { noteId: currentNoteId });
  }

  currentNoteId = noteId;

  // Set up event listeners
  if (callbacks.onJoined) {
    socket.once('joined-room', callbacks.onJoined);
  }

  if (callbacks.onMessage) {
    socket.on('receive-message', callbacks.onMessage);
  }

  if (callbacks.onError) {
    socket.on('error', callbacks.onError);
  }

  if (callbacks.onUserJoined) {
    socket.on('user-joined', callbacks.onUserJoined);
  }

  // Join the room
  socket.emit('join-note-chat', { noteId });
  console.log('Joining note chat room:', noteId);
};

/**
 * Send a message via socket
 */
const sendMessage = (noteId, message, callbacks = {}) => {
  if (!socket || !socket.connected) {
    console.error('Socket not connected');
    if (callbacks.onError) {
      callbacks.onError({ message: 'Not connected to chat server' });
    }
    return;
  }

  socket.emit('send-message', { noteId, message }, (response) => {
    if (response && response.error) {
      if (callbacks.onError) {
        callbacks.onError(response);
      }
    } else if (callbacks.onSuccess) {
      callbacks.onSuccess(response);
    }
  });
};

/**
 * Leave a note chat room
 */
const leaveNoteChat = (noteId) => {
  if (socket && socket.connected) {
    socket.emit('leave-note-chat', { noteId });
    socket.off('receive-message');
    socket.off('user-joined');
    socket.off('error');
    if (currentNoteId === noteId) {
      currentNoteId = null;
    }
    console.log('Left note chat room:', noteId);
  }
};

/**
 * Disconnect socket
 */
const disconnectSocket = () => {
  if (socket) {
    socket.disconnect();
    socket = null;
    currentNoteId = null;
    console.log('Socket disconnected');
  }
};

/**
 * Check if socket is connected
 */
const isConnected = () => {
  return socket && socket.connected;
};

export default {
  initializeSocket,
  joinNoteChat,
  sendMessage,
  leaveNoteChat,
  disconnectSocket,
  isConnected,
  getSocket: () => socket,
};

